/** Az Elemzés időszakai budapesti időben (téli-nyári időszámítással). A felső határ kizáró: `created_at < ig`. */
const IDOZONA = 'Europe/Budapest';

export type IdoszakTipus = '7nap' | '30nap' | 'ez_honap' | 'elozo_honap' | 'egyedi';
export interface Idoszak {
  /** Az első nap budapesti éjfele UTC-ben (ISO). */
  tol: string;
  /** Az utolsó nap utáni nap budapesti éjfele UTC-ben (ISO), kizáró határ. */
  ig: string;
  /** Az első és az utolsó nap, `ÉÉÉÉ-HH-NN` (budapesti naptári nap). */
  tolNap: string;
  utolsoNap: string;
}

const NAP_MINTA = /^(\d{4})-(\d{2})-(\d{2})$/;

function napReszek(nap: string): [number, number, number] {
  const m = NAP_MINTA.exec(nap);
  if (!m) throw new RangeError(`Érvénytelen nap: ${nap}`);
  const [e, h, n] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = new Date(Date.UTC(e, h - 1, n));
  if (d.getUTCFullYear() !== e || d.getUTCMonth() !== h - 1 || d.getUTCDate() !== n) throw new RangeError(`Érvénytelen nap: ${nap}`);
  return [e, h, n];
}

function napSzoveg(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function napHozzaad(nap: string, db: number): string {
  const [e, h, n] = napReszek(nap);
  return napSzoveg(new Date(Date.UTC(e, h - 1, n + db)));
}

/** A budapesti időzóna eltolása (ms) az adott pillanatban. */
function eltolas(pillanat: number): number {
  const r: Record<string, number> = {};
  for (const p of new Intl.DateTimeFormat('en-US', {
    timeZone: IDOZONA, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
  }).formatToParts(new Date(pillanat))) {
    if (p.type !== 'literal') r[p.type] = Number(p.value);
  }
  return Date.UTC(r.year!, r.month! - 1, r.day!, r.hour!, r.minute!, r.second!) - Math.floor(pillanat / 1000) * 1000;
}

/** A budapesti nap kezdete (éjfél) UTC-ben, ISO-szövegként. */
export function budapestiNapEleje(nap: string): string {
  const [e, h, n] = napReszek(nap);
  const helyi = Date.UTC(e, h - 1, n);
  // Két lépés: az éjféli átállás-közeli esetekben a második eltolás a pontos.
  const elso = helyi - eltolas(helyi);
  return new Date(helyi - eltolas(elso)).toISOString();
}

/** A mai nap budapesti naptári napja. */
export function budapestiMaiNap(most: Date): string {
  const r: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-CA', { timeZone: IDOZONA, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(most)) {
    r[p.type] = p.value;
  }
  return `${r.year}-${r.month}-${r.day}`;
}

function idoszakNapokbol(tolNap: string, utolsoNap: string): Idoszak {
  return { tolNap, utolsoNap, tol: budapestiNapEleje(tolNap), ig: budapestiNapEleje(napHozzaad(utolsoNap, 1)) };
}

export function idoszakSzamitas(tipus: IdoszakTipus, most: Date, egyedi?: { tol: string; ig: string }): Idoszak {
  const ma = budapestiMaiNap(most);
  if (tipus === '7nap') return idoszakNapokbol(napHozzaad(ma, -6), ma);
  if (tipus === '30nap') return idoszakNapokbol(napHozzaad(ma, -29), ma);
  const [e, h] = napReszek(ma);
  if (tipus === 'ez_honap' || tipus === 'elozo_honap') {
    const eltolasHonap = tipus === 'ez_honap' ? 0 : -1;
    const eleje = new Date(Date.UTC(e, h - 1 + eltolasHonap, 1));
    const vege = new Date(Date.UTC(e, h + eltolasHonap, 0));
    return idoszakNapokbol(napSzoveg(eleje), napSzoveg(vege));
  }
  if (!egyedi?.tol || !egyedi.ig) throw new Error('Add meg a kezdő és a záró napot.');
  napReszek(egyedi.tol);
  napReszek(egyedi.ig);
  if (egyedi.tol > egyedi.ig) throw new Error('A kezdő nap nem lehet a záró nap után.');
  return idoszakNapokbol(egyedi.tol, egyedi.ig);
}

const HONAPNEV = ['január', 'február', 'március', 'április', 'május', 'június', 'július', 'augusztus', 'szeptember', 'október', 'november', 'december'];

/** A nap ragja („8-án”, „1-jén”): a magyar számnév utolsó magánhangzója szerint. */
const NAP_RAG = ['-jén', '-án', '-án', '-én', '-én', '-án', '-én', '-án', '-én', '-én', '-én', '-én', '-án', '-én', '-én', '-án', '-én', '-án', '-én', '-án', '-én', '-én', '-án', '-én', '-én', '-án', '-én', '-án', '-én', '-án', '-én'];

function nagybetus(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** „Szeptember 9. és október 8. között” (egy napnál „Október 8-án”). */
export function idoszakMondat(tolNap: string, utolsoNap: string): string {
  const [, th, tn] = napReszek(tolNap);
  const [, uh, un] = napReszek(utolsoNap);
  if (tolNap === utolsoNap) return `${nagybetus(HONAPNEV[uh - 1]!)} ${un}${NAP_RAG[un - 1]}`;
  return `${nagybetus(HONAPNEV[th - 1]!)} ${tn}. és ${HONAPNEV[uh - 1]!} ${un}. között`;
}
